import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { UserPlus, Loader2, CheckCircle2, Copy } from 'lucide-react';
import { log } from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only "Add User" modal. Creates an account + emails a welcome
 * link. Returns the welcome link to the admin so they can copy it as a
 * fallback if the email is delayed (e.g., SMTP throttled).
 */
export const InviteUserModal = ({ open, onClose, onCreated }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [pricingTier, setPricingTier] = useState('evergreen');
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null); // { welcome_link, email }

  const resetForm = () => {
    setEmail(''); setFirstName(''); setLastName('');
    setCompany(''); setPhone(''); setPricingTier('evergreen');
    setIsAdmin(false);
    setErr(null); setResult(null);
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/users/admin-invite`, {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        company: company.trim() || null,
        phone: phone.trim() || null,
        pricing_tier: pricingTier,
        is_admin: isAdmin,
      });
      setResult(r.data);
      onCreated?.();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      log.error('Admin invite failed:', error);
      setErr(detail || 'Could not create user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.welcome_link);
    } catch {
      // Clipboard API blocked (older browsers / insecure context) — fall
      // back to a selection hint so the admin can copy manually.
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg bg-white" data-testid="invite-user-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#2F5D3A] flex items-center gap-2">
            <UserPlus className="w-6 h-6" />
            Invite a New User
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-1">
            They'll receive an EG3-branded welcome email with a link to set their password.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 mt-2" data-testid="invite-user-success">
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-md">
              <CheckCircle2 className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-900">
                  Account created for {result.email}
                </p>
                <p className="text-xs text-green-800 mt-1">
                  A welcome email is on its way. They'll land on the app signed in
                  once they pick a password.
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-600">Backup welcome link</Label>
              <p className="text-[11px] text-gray-500 mb-1">
                Copy this in case the email doesn't arrive — valid for 7 days.
              </p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={result.welcome_link}
                  className="font-mono text-xs"
                  data-testid="invite-user-welcome-link"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  type="button"
                  onClick={copyLink}
                  variant="outline"
                  className="border-[#4A7C59] text-[#4A7C59] hover:bg-green-50"
                  data-testid="invite-user-copy-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); }}
                className="flex-1"
                data-testid="invite-user-another-btn"
              >
                Invite another
              </Button>
              <Button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-[#4A7C59] hover:bg-[#3D6A4A] text-white"
                data-testid="invite-user-done-btn"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="invite-user-form">
            <div>
              <Label htmlFor="inv-email">Email <span className="text-red-500">*</span></Label>
              <Input id="inv-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="inv-first">First name <span className="text-red-500">*</span></Label>
                <Input id="inv-first" required value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="invite-first-name" />
              </div>
              <div>
                <Label htmlFor="inv-last">Last name <span className="text-red-500">*</span></Label>
                <Input id="inv-last" required value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="invite-last-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="inv-company">Company</Label>
                <Input id="inv-company" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="invite-company" />
              </div>
              <div>
                <Label htmlFor="inv-phone">Phone</Label>
                <Input id="inv-phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="invite-phone" />
              </div>
            </div>
            <div>
              <Label htmlFor="inv-tier">Pricing tier</Label>
              <select
                id="inv-tier"
                value={pricingTier}
                onChange={(e) => setPricingTier(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A7C59]"
                data-testid="invite-tier"
              >
                <option value="evergreen">Evergreen (Strategic Partner — 30% off)</option>
                <option value="stickeryou">Standard (retail)</option>
              </select>
            </div>

            <label
              className="flex items-start gap-3 p-3 rounded-md border border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
              data-testid="invite-admin-label"
            >
              <input
                type="checkbox"
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                data-testid="invite-is-admin-checkbox"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900">
                  Grant admin privileges
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Full access to the Admin Dashboard: custom orders, AR aging,
                  user management, pricing imports. Use sparingly.
                </div>
              </div>
            </label>

            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="invite-error">
                {err}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-gray-300"
                onClick={handleClose}
                disabled={submitting}
                data-testid="invite-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#2F5D3A] text-white font-semibold"
                data-testid="invite-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</>
                ) : 'Create & Send Invite'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserModal;
